import { create } from 'zustand';
import { favoritesService as favoritesApi } from '../services/favoritesService';
import type { FavoriteVerse } from '../lib/types';

// Helper to normalize verse IDs and extract parts
const normalizeVerseId = (verseId: string) => {
  // Handle JHN.12.25 format
  if (verseId.includes('.') && !verseId.includes(':')) {
    const [book, chapter, verse] = verseId.split('.');
    // Map common abbreviations to full names
    const bookMap: Record<string, string> = {
      'GEN': 'Genesis',
      'EXO': 'Exodus',
      'LEV': 'Leviticus',
      'NUM': 'Numbers',
      'DEU': 'Deuteronomy',
      'JOS': 'Joshua',
      'JDG': 'Judges',
      'RUT': 'Ruth',
      '1SA': '1 Samuel',
      '2SA': '2 Samuel',
      '1KI': '1 Kings',
      '2KI': '2 Kings',
      '1CH': '1 Chronicles',
      '2CH': '2 Chronicles',
      'EZR': 'Ezra',
      'NEH': 'Nehemiah',
      'EST': 'Esther',
      'JOB': 'Job',
      'PSA': 'Psalms',
      'PRO': 'Proverbs',
      'ECC': 'Ecclesiastes',
      'SNG': 'Song of Solomon',
      'ISA': 'Isaiah',
      'JER': 'Jeremiah',
      'LAM': 'Lamentations',
      'EZK': 'Ezekiel',
      'DAN': 'Daniel',
      'HOS': 'Hosea',
      'JOL': 'Joel',
      'AMO': 'Amos',
      'OBA': 'Obadiah',
      'JON': 'Jonah',
      'MIC': 'Micah',
      'NAM': 'Nahum',
      'HAB': 'Habakkuk',
      'ZEP': 'Zephaniah',
      'HAG': 'Haggai',
      'ZEC': 'Zechariah',
      'MAL': 'Malachi',
      'MAT': 'Matthew',
      'MRK': 'Mark',
      'LUK': 'Luke',
      'JHN': 'John',
      'ACT': 'Acts',
      'ROM': 'Romans',
      '1CO': '1 Corinthians',
      '2CO': '2 Corinthians',
      'GAL': 'Galatians',
      'EPH': 'Ephesians',
      'PHP': 'Philippians',
      'COL': 'Colossians',
      '1TH': '1 Thessalonians',
      '2TH': '2 Thessalonians',
      '1TI': '1 Timothy',
      '2TI': '2 Timothy',
      'TIT': 'Titus',
      'PHM': 'Philemon',
      'HEB': 'Hebrews',
      'JAS': 'James',
      '1PE': '1 Peter',
      '2PE': '2 Peter',
      '1JN': '1 John',
      '2JN': '2 John',
      '3JN': '3 John',
      'JUD': 'Jude',
      'REV': 'Revelation'
    };
    const fullBook = bookMap[book] || book;
    return {
      id: `${fullBook.toLowerCase()}_${chapter}_${verse}`,
      book: fullBook,
      chapter: parseInt(chapter),
      verse: parseInt(verse)
    };
  }
  
  // Handle existing underscore format (genesis_1_1)
  if (verseId.includes('_') && !verseId.includes(':')) {
    const [book, chapter, verse] = verseId.split('_');
    const formattedBook = book.charAt(0).toUpperCase() + book.slice(1);
    return {
      id: verseId,
      book: formattedBook,
      chapter: parseInt(chapter),
      verse: parseInt(verse)
    };
  }

  // Handle "Jeremiah 29:11_KJV" format
  if (verseId.includes(':')) {
    // Remove version suffix if present
    const baseVerseId = verseId.split('_')[0];
    const [bookAndChapter, verse] = baseVerseId.split(':');
    const [book, chapter] = bookAndChapter.split(' ');
    return {
      id: `${book.toLowerCase()}_${chapter}_${verse}`,
      book: book,
      chapter: parseInt(chapter),
      verse: parseInt(verse)
    };
  }

  // Return original if format is unknown
  return {
    id: verseId,
    book: '',
    chapter: 0,
    verse: 0
  };
};

interface FavoritesStore {
  favorites: Record<string, FavoriteVerse>;
  isLoading: boolean;
  error: string | null;
  toggleFavorite: (verse: Omit<FavoriteVerse, 'createdAt'>) => Promise<void>;
  isFavorite: (verseId: string) => Promise<boolean>;
  getFavorites: () => Promise<FavoriteVerse[]>;
  loadFavorites: () => Promise<void>;
}

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
  favorites: {},
  isLoading: false,
  error: null,

  toggleFavorite: async (verse) => {
    try {
      set({ isLoading: true, error: null });
      const normalized = normalizeVerseId(verse.id);
      const isFavorited = await favoritesApi.isFavorite(normalized.id);
      
      if (isFavorited) {
        await favoritesApi.removeFavorite(normalized.id);
        set((state) => {
          const newFavorites = { ...state.favorites };
          delete newFavorites[normalized.id];
          return { favorites: newFavorites, isLoading: false };
        });
      } else {
        await favoritesApi.addFavorite({
          ...verse,
          id: normalized.id,
          book: normalized.book || verse.book,
          chapter: normalized.chapter || verse.chapter,
          verse: normalized.verse || verse.verse,
          version: verse.version || 'kjv',
          text: verse.text || ''
        });
        const favorite = await favoritesApi.getFavorite(normalized.id);
        if (favorite) {
          set((state) => ({
            favorites: { ...state.favorites, [normalized.id]: favorite },
            isLoading: false
          }));
        }
      }
    } catch (error) {
      set({ error: 'Failed to update favorite', isLoading: false });
      console.error('Failed to update favorite:', error);
    }
  },

  isFavorite: async (verseId) => {
    try {
      const normalized = normalizeVerseId(verseId);
      return await favoritesApi.isFavorite(normalized.id);
    } catch (error) {
      set({ error: 'Failed to check favorite status' });
      console.error('Failed to check favorite status:', error);
      return false;
    }
  },

  getFavorites: async () => {
    try {
      const favorites = await favoritesApi.getAllFavorites();
      return favorites.map(favorite => {
        const normalized = normalizeVerseId(favorite.id);
        return {
          ...favorite,
          book: normalized.book,
          chapter: normalized.chapter,
          verse: normalized.verse,
          version: 'kjv', // Default to KJV if not specified
          reference: `${normalized.book} ${normalized.chapter}:${normalized.verse}`,
          text: favorite.text || ''
        };
      });
    } catch (error) {
      set({ error: 'Failed to get favorites' });
      console.error('Failed to get favorites:', error);
      return [];
    }
  },

  loadFavorites: async () => {
    try {
      set({ isLoading: true, error: null });
      const favorites = await favoritesApi.getAllFavorites();
      const favoritesMap = favorites.reduce((acc, favorite) => {
        const normalized = normalizeVerseId(favorite.id);
        acc[normalized.id] = {
          ...favorite,
          book: normalized.book,
          chapter: normalized.chapter,
          verse: normalized.verse,
          version: 'kjv', // Default to KJV if not specified
          reference: `${normalized.book} ${normalized.chapter}:${normalized.verse}`,
          text: favorite.text || ''
        };
        return acc;
      }, {} as Record<string, FavoriteVerse>);
      set({ favorites: favoritesMap, isLoading: false });
    } catch (error) {
      set({ error: 'Failed to load favorites', isLoading: false });
      console.error('Failed to load favorites:', error);
    }
  }
}));
